import InfiniteScroll from 'react-infinite-scroll-component';
import Title from '../controls/Title';
import AccessImageCard from './AccessImageCard';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, LinearProgress, Button } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getData } from '../../api/dataSlice';
import { useGetAccessMonitoringQuery } from '../../api/graphqlApi';
import { getVehicleKey } from './VehicleStats';
import { useAccessFooterState } from './useAccessFooterState';
import { isMobile } from 'react-device-detect';

const numImagesToLoadAtScroll = 10

export default function AccessContent(props) {

    const { camera, direction: directionProp } = props
    const { t } = useTranslation()
    const { accessMonitoringCameraId, accessMonitoringVehicleFilter } = useSelector(getData)
    const { data: accessMonitoringData } = useGetAccessMonitoringQuery()
    // derive `selectedDay` from the access-normalized timestamp (same source as the DatePicker in
    // AccessAccordion) so the filter matches what the picker shows. Using Redux `timestamp` here
    // directly breaks on /mysite where `ArchiveControls` dispatches the archive's last-image
    // timestamp on load — if that day has no access events the UI shows today in the picker but
    // filters by the stale archive day, yielding "No data" until the user flips the date.
    // `isActive=false` keeps this purely derivative — AccessAccordion owns the side-effects.
    const { currentDay } = useAccessFooterState(false)

    const [visibleCount, setVisibleCount] = useState(numImagesToLoadAtScroll)
    const [loadedImages, setLoadedImages] = useState(() => new Set())
    const [failedImages, setFailedImages] = useState(() => new Set())
    const [internalDirection, setInternalDirection] = useState('entry')

    const cameraId = accessMonitoringCameraId ?? camera?.id
    const fallbackUrl = camera?.image ?? ""
    const scrollRef = useRef(null)
    const direction = directionProp ?? internalDirection
    const canSwitch = !directionProp // mobile: manage direction internally

    const baseUrl = useMemo(() => {
        const download = camera?.download
        if (!download) return "" // prevent getting "undefinedbest_frames/"
        return `${download.replace("zeitraffer", "einfahrt")}best_frames/`
    }, [camera?.download])

    const allEvents = useMemo(() => (
        accessMonitoringData?.byCameraId?.[cameraId] ?? []
    ), [accessMonitoringData, cameraId])

    const selectedDay = currentDay // see comment above on the `useAccessFooterState(false)` call

    const vehicleFilterSet = useMemo(
        () => accessMonitoringVehicleFilter ? new Set(accessMonitoringVehicleFilter) : null,
        [accessMonitoringVehicleFilter]
    )

    const dayEvents = useMemo(() => {
        const filtered = allEvents
            .filter(elem => typeof elem?.timestamp === "string")
            .filter(elem => elem?.timestamp?.slice(0, 10) === selectedDay) // faster than dayjs comparison
            .filter(elem => elem?.direction === direction)
            .filter(elem => !vehicleFilterSet || vehicleFilterSet.has(getVehicleKey(elem)))
            .filter(elem => !failedImages.has(elem.id))

        return filtered.slice().sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)) // newest first (descending)
    }, [allEvents, selectedDay, direction, vehicleFilterSet, failedImages])

    const visibleEvents = useMemo(() => (
        dayEvents.slice(0, visibleCount)
    ), [dayEvents, visibleCount])

    const pendingVisibleCount = useMemo(() => {
        let notLoaded = 0
        for (const elem of visibleEvents) {
            if (!loadedImages.has(elem.id)) notLoaded++
        }
        return notLoaded
    }, [visibleEvents, loadedImages])

    const setStateWithoutDoubles = useCallback((setState) => (id) => {
        setState(prev => {
            if (prev.has(id)) return prev
            const next = new Set(prev)
            next.add(id)
            return next
        })
    }, [])

    const markLoaded = useMemo(() => setStateWithoutDoubles(setLoadedImages), [setStateWithoutDoubles])
    const markFailed = useMemo(() => setStateWithoutDoubles(setFailedImages), [setStateWithoutDoubles])

    const getVehicleLabel = useCallback((event) => {
        const subtype = event?.vehicleSubtype?.name
        if (subtype) return t(`pages.access.vehicle.subtypes.${subtype}`, subtype)

        const type = event?.vehicleType?.name
        if (type) return t(`pages.access.vehicle.types.${type}`, type)

        return ""
    }, [t]) 

    const hasMoreValue = visibleCount < dayEvents.length

    const handleOnRowsScrollEnd = useCallback(() => {
        if (!hasMoreValue) return
        setVisibleCount(prev => Math.min(prev + numImagesToLoadAtScroll, dayEvents.length))
    }, [hasMoreValue, dayEvents.length])

    // reset tracker if day/direction/camera changes
    useEffect(() => {
        setVisibleCount(numImagesToLoadAtScroll)
        setLoadedImages(new Set())
        setFailedImages(new Set())
        if (scrollRef.current) scrollRef.current.scrollTop = 0
    }, [selectedDay, direction, cameraId])

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            alignItems: 'center',
            minHeight: 0
        }}>

            {canSwitch ? (
                <Box className="Title" sx={{ left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                    {direction === 'exit' && (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<ArrowBackIos />}
                            onClick={() => setInternalDirection('entry')}
                            sx={{ position: 'absolute', left: 6, top: 3, minWidth: 0, minHeight: 25, pl: 1, pr: 0.75, py: 0.25, fontSize: '0.75rem', lineHeight: 1, '& .MuiButton-startIcon': { mr: 0.25 }, '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: '0.75rem' } }}
                        >
                            {t("pages.access.entry")}
                        </Button>
                    )}

                    <Box className="Title-Label">
                        <Typography variant="body1" className="Title-CameraLabel">
                            {direction === "entry" ? t("pages.access.entry") : t("pages.access.exit")}
                        </Typography>
                    </Box>
                    
                    {direction === 'entry' && (
                        <Button
                            size="small"
                            variant="contained"
                            endIcon={<ArrowForwardIos />}
                            onClick={() => setInternalDirection('exit')}
                            sx={{ position: 'absolute', right: 6, top: 3, minWidth: 0, minHeight: 25, pl: 1, pr: 0.75, py: 0.25, fontSize: '0.75rem', lineHeight: 1, '& .MuiButton-endIcon': { ml: 0.25 }, '& .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: '0.75rem' } }}
                        >
                            {t("pages.access.exit")}
                        </Button>
                    )}
                </Box>
            ) : (
                <Title cameraName={direction === "entry" ? t("pages.access.entry") : t("pages.access.exit")} />
            )}

            <Box
                ref={scrollRef}
                id={`infinite-scroll-${direction}`}
                className="thin-scrollbar"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    mt: "34px",
                    mr: isMobile ? 0.5 : 1
                }}
            >

                {/* prevent hiding scrollbar */}

                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    pl: 0.5,
                    pr: isMobile ? 0 : 0.5
                }}> 

                    <InfiniteScroll
                        dataLength={visibleEvents.length}
                        next={handleOnRowsScrollEnd}
                        hasMore={hasMoreValue}
                        scrollThreshold={0.8}
                        scrollableTarget={`infinite-scroll-${direction}`}
                        loader={null}
                        style={{ overflow: 'unset' }} // allow sticky headers or proper layout if needed
                    >

                        {visibleEvents.map((elem, index) => {
                            const filename = elem.bestImageName
                            const imgSrc = filename ? `${baseUrl}${filename}` : fallbackUrl

                            return (
                                <AccessImageCard
                                    key={elem.id}
                                    elem={elem}
                                    imgSrc={imgSrc}
                                    isLoaded={loadedImages.has(elem.id)}
                                    vehicleLabel={getVehicleLabel(elem)}
                                    onLoaded={markLoaded}
                                    onFailed={markFailed}
                                    t={t}
                                    allEvents={dayEvents}
                                    currentIndex={index}
                                    baseUrl={baseUrl}
                                    fallbackUrl={fallbackUrl}
                                    getVehicleLabel={getVehicleLabel}
                                    direction={direction}
                                />
                            )
                        })}
                    </InfiniteScroll>

                    {dayEvents.length > 0 && pendingVisibleCount > 0 && (
                        <LinearProgress sx={{ mt: 0.5, mb: 1.5 }} />
                    )}

                    {accessMonitoringData && dayEvents.length === 0 && (
                        <Box sx={{
                            minHeight: isMobile ? "auto" : "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pt: isMobile ? 0.5 : 0,
                            pb: isMobile ? 1 : 0
                        }}>
                            <Typography sx={{ color: 'white', userSelect: 'none' }}>
                                {t("msg.no_data")}
                            </Typography>
                        </Box>
                    )}

                </Box>
            </Box>

        </Box>
    )
}
